import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { MediaPlayerInstance } from '@vidstack/react';

import { LoadingScreen } from '../LoadingScreen/LoadingScreen';
import {
  VideoPlayerContent,
  getBlobDuration,
  getMimeTypeFromFilename,
} from '../TestRecordingViewer/TestRecordingViewer';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/Icon.type';
import { Text } from '../Text/Text';
import api from '../../utils/axios';
import { getRecordingDetails } from '../../services/testRecordingService';

const TestRecordingViewerPage: React.FC = () => {
  const { projectId, recordingId } = useParams<{ projectId: string; recordingId: string }>();
  const playerRef = useRef<MediaPlayerInstance>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string>('video/webm');
  const [duration, setDuration] = useState<number | undefined>(undefined);
  // Title comes from the server (endpoint), never from the URL.
  const [title, setTitle] = useState<string>('Test Recording');

  const videoUrlRef = useRef<string | null>(null);
  useEffect(() => {
    videoUrlRef.current = videoUrl;
  }, [videoUrl]);

  useEffect(() => {
    if (!projectId || !recordingId) {
      setError('Invalid recording link');
      setLoading(false);
      return;
    }
    loadRecording();
    return () => {
      if (videoUrlRef.current) {
        URL.revokeObjectURL(videoUrlRef.current);
      }
    };
  }, [projectId, recordingId]);

  const loadRecording = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      // Force revalidation so the server re-runs the permission check on every load
      const headers: Record<string, string> = {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      try {
        const details = await getRecordingDetails(projectId as string, recordingId as string);
        if (details?.title) setTitle(details.title);
      } catch (detailsErr) {
        console.warn('Could not load recording details:', detailsErr);
      }

      const response = await api.get(
        `/browser-stream/recordings/${encodeURIComponent(projectId as string)}/${encodeURIComponent(recordingId as string)}`,
        { responseType: 'blob', headers, params: { t: Date.now() } },
      );

      const blob = response.data as Blob;
      if (blob.size === 0) {
        setError('Recording is being processed, please try again shortly');
        return;
      }
      setMimeType(blob.type || getMimeTypeFromFilename(''));
      setDuration(await getBlobDuration(blob));
      setVideoUrl(URL.createObjectURL(blob));
    } catch (err: any) {
      console.error('Error loading test recording:', err);
      if (err.response?.status === 404) {
        setError('Recording not found or you do not have access to it');
      } else if (err.response?.status === 401) {
        setError('You are not authorized to view this recording');
      } else {
        setError('Failed to load recording. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen w-screen">
        <LoadingScreen />
      </div>
    );
  }

  if (error || !videoUrl) {
    return (
      <div className="h-screen w-screen bg-bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="text-text-error-primary text-lg font-medium mb-2">Error</div>
          <div className="text-text-secondary mb-4">{error || 'Recording not found'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full bg-bg-primary flex justify-center overflow-y-auto">
      <div className="w-full flex justify-center px-bg-v2-xl py-bg-v2-3xl">
        {/* Title + player share one width so the header aligns to the player's
            left edge. Cap the width so the player's height (aspect-video) never
            pushes its bottom control bar below the viewport. */}
        <div className="w-full max-w-[min(1200px,calc((100vh_-_200px)*16/9))] flex flex-col gap-bg-v2-xl">
          {/* Title header — icon + left-aligned title */}
          <div className="flex items-center gap-bg-v2-md w-full">
            <div className="flex items-center justify-center w-11 h-11 rounded-bg-v2-md bg-bg-brand-primary-alt shrink-0">
              <Icon name={IconName.VideoRecording} size="md" variant="brand-secondary" />
            </div>
            <Text variant="display-xs" weight="semibold" color="primary" align="left" className="truncate">
              {title}
            </Text>
          </div>

          {/* Video player */}
          <div className="w-full rounded-bg-v2-lg overflow-hidden border border-border-secondary bg-black shadow-bg-v2-lg">
            <VideoPlayerContent
              videoUrl={videoUrl}
              mimeType={mimeType}
              playerRef={playerRef}
              duration={duration}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestRecordingViewerPage;
